export type AppEnv = 'dev' | 'prod';

const ENV_URLS: Record<AppEnv, { usersApi: string; dataApi: string }> = {
  // Dev points at the single Firebase App Hosting backend (telcofy-norway-poc,
  // `main` branch): the old split Cloud Run hosts (dev.users.api / dev.data.api)
  // collapse into one origin with an `/api` prefix, so `${BASE}/maps` becomes
  // `backend.telcofy.ai/api/maps`. Staying cross-origin is intentional — the
  // Bearer-token flow is retained; the same-origin `/api/**` rewrite is a later
  // phase, not this change. Trello #416.
  dev: {
    usersApi: 'https://backend.telcofy.ai/api',
    dataApi: 'https://backend.telcofy.ai/api',
  },
  // Prod uses the prod App Hosting backend (telcofy-norway-delivery,
  // `delivery` branch), the same single-origin `/api` layout as dev. It replaces
  // the old split Cloud Run hosts (prod.users.api / prod.data.api), so new
  // routes no longer have to exist on two prod backends. Trello #416.
  prod: {
    usersApi: 'https://prod.backend.telcofy.ai/api',
    dataApi: 'https://prod.backend.telcofy.ai/api',
  },
};

const resolveEnv = (): AppEnv => {
  const raw = process.env.REACT_APP_ENV?.toLowerCase();
  // Accept both "prod" and "production" — the CI/build scripts have used
  // both spellings, and only an exact "prod" used to flip the env.
  return raw === 'prod' || raw === 'production' ? 'prod' : 'dev';
};

export const CURRENT_ENV: AppEnv = resolveEnv();

// Frontend always talks to the env-explicit hosts (prod.* / dev.*). The bare
// data.api / users.api hosts are the customer-facing switchable aliases and
// are intentionally NOT used here.
export const USERS_API_BASE_URL =
  process.env.REACT_APP_USERS_API_URL ?? ENV_URLS[CURRENT_ENV].usersApi;

export const DATA_API_BASE_URL =
  process.env.REACT_APP_DATA_API_URL ?? ENV_URLS[CURRENT_ENV].dataApi;

// Realtime data is split per GCP project (poc for dev, delivery for prod) while
// Firestore map metadata is shared across both environments. This is the project
// whose BigQuery THIS env reads realtime data from — used to grey out saved areas
// whose data lives in the other env. See src/utils/realtimeEnv.ts + card #425.
export const REALTIME_BQ_PROJECT_ID =
  CURRENT_ENV === 'prod' ? 'telcofy-norway-delivery' : 'telcofy-norway-poc';
